import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Alert,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  MailOutline,
  LockOutlined,
  ArrowForward,
  CheckCircle,
  Security,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';

/** Colores oficiales Kobra IA / Bankivo Assist */
const C = {
  navy: '#121826',
  blue: '#4185F1',
  green: '#6BD56F',
};

const INPUT_BG = '#EEF1F8';
const PAGE_BG_TOP = '#EEF4FF';
const PAGE_BG_BOTTOM = '#F5F7FB';

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/images/logotipo_kobraIA_removebg.png`;

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { showToast, ToastComponent } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      showToast('Inicio de sesión exitoso', 'success');
      navigate('/inbox');
    } catch (err) {
      setError(err.message || 'Correo electrónico o contraseña inválidos');
      showToast(err.message || 'Error al iniciar sesión', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSsoClick = () => {
    showToast('Inicio de sesión con SSO disponible próximamente', 'info');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(180deg, ${PAGE_BG_TOP} 0%, ${PAGE_BG_BOTTOM} 55%, #eef1f6 100%)`,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse 85% 55% at 50% -10%, rgba(65, 133, 241, 0.18), transparent 55%),
            radial-gradient(ellipse 70% 50% at 100% 100%, rgba(107, 213, 111, 0.08), transparent 45%)`,
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          opacity: 0.45,
          backgroundImage: `repeating-radial-gradient(
            circle at 50% 40%,
            transparent 0,
            transparent 48px,
            rgba(65, 133, 241, 0.045) 48px,
            rgba(65, 133, 241, 0.045) 49px
          )`,
          pointerEvents: 'none',
        },
      }}
    >
      <ToastComponent />
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: { xs: 3, sm: 4 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Container maxWidth="sm">
          {/* Brand above card */}
          <Box sx={{ textAlign: 'center', mb: 0 }}>
            <Box
              component="img"
              src={LOGO_SRC}
              alt="Kobra IA by Bankivo"
              sx={{
                height: { xs: 90, sm: 90 },
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
                display: 'block',
                mx: 'auto',
                mt: 1.5,
              }}
            />
           
           
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              width: '100%',
              mt: 3,
              maxWidth: 440,
              mx: 'auto',
              borderRadius: 2,
              backgroundColor: '#fff',
              border: '1px solid rgba(18, 24, 38, 0.06)',
              boxShadow:
                '0 4px 24px rgba(18, 24, 38, 0.06), 0 24px 48px rgba(65, 133, 241, 0.08)',
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: C.navy,
                  letterSpacing: '-0.02em',
                  mb: 0.5,
                }}
              >
                Iniciar sesión
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(18, 24, 38, 0.55)', lineHeight: 1.5 }}
              >
                Introduce tus credenciales para acceder a tu panel seguro.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: C.navy,
                    mb: 1,
                    fontSize: '0.8125rem',
                  }}
                >
                  Correo electrónico
                </Typography>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  placeholder="tu@email.com"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutline sx={{ color: 'rgba(18,24,38,0.38)', fontSize: 22 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={textFieldSx}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Link
                  href="#"
                  variant="body2"
                  onClick={(e) => e.preventDefault()}
                  sx={{
                    color: C.blue,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: C.navy,
                    mb: 1,
                    fontSize: '0.8125rem',
                  }}
                >
                  Contraseña
                </Typography>
                <TextField
                  fullWidth
                  name="password"
                  placeholder="••••••••"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: 'rgba(18,24,38,0.38)', fontSize: 22 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="mostrar u ocultar contraseña"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          sx={{ color: 'rgba(18,24,38,0.45)' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={textFieldSx}
                />
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      color: 'rgba(18,24,38,0.35)',
                      '&.Mui-checked': { color: C.blue },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: C.navy, fontWeight: 500 }}>
                    Recordarme en este dispositivo
                  </Typography>
                }
                sx={{ mb: 2.5, ml: -0.5 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={loading}
                endIcon={<ArrowForward sx={{ fontSize: 20, color: 'inherit' }} />}
                sx={{
                  py: 1.35,
                  borderRadius: 1.5,
                  fontWeight: 700,
                  fontSize: '0.9375rem',
                  textTransform: 'none',
                  // Theme MuiButton overrides primary+contained with gray gradient — win specificity
                  '&&': {
                    borderRadius: 1.5,
                    color: '#fff',
                    backgroundColor: C.blue,
                    backgroundImage: 'none',
                    border: 'none',
                    boxShadow: `0 8px 24px ${C.blue}44`,
                    '&:hover': {
                      backgroundColor: '#3574d9',
                      backgroundImage: 'none',
                      boxShadow: `0 12px 28px ${C.blue}55`,
                      border: 'none',
                    },
                    '&:active': {
                      backgroundColor: '#2d6bc7',
                      backgroundImage: 'none',
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(65, 133, 241, 0.45)',
                      backgroundImage: 'none',
                      color: '#fff',
                    },
                  },
                }}
              >
                {loading ? 'Iniciando sesión…' : 'Iniciar sesión'}
              </Button>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  my: 3,
                }}
              >
                <Divider sx={{ flex: 1, borderColor: 'rgba(18, 24, 38, 0.12)' }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(18, 24, 38, 0.45)',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                  }}
                >
                  O
                </Typography>
                <Divider sx={{ flex: 1, borderColor: 'rgba(18, 24, 38, 0.12)' }} />
              </Box>

              

              <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  mt: 3,
                  color: 'rgba(18, 24, 38, 0.6)',
                }}
              >
                ¿No tienes cuenta?{' '}
                <Link
                  component={RouterLink}
                  to="/onBoarding"
                  sx={{
                    color: C.blue,
                    fontWeight: 700,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Crear cuenta
                </Link>
              </Typography>
            </Box>
          </Paper>

          {/* Payment protected badge */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 3,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2.5,
                py: 1,
                borderRadius: 999,
                backgroundColor: C.green,
                boxShadow: `0 4px 16px ${C.green}55`,
              }}
            >
              <CheckCircle sx={{ fontSize: 20, color: C.navy }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  color: C.navy,
                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                  lineHeight: 1.2,
                }}
              >
                PAGOS PROTEGIDOS POR KOBRA IA
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Page footer */}
      <Box
        component="footer"
        sx={{
          position: 'relative',
          zIndex: 1,
          px: 2,
          py: 2.5,
          borderTop: '1px solid rgba(18, 24, 38, 0.06)',
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'center', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'rgba(18, 24, 38, 0.45)', textAlign: { xs: 'center', sm: 'left' } }}
            >
              © {new Date().getFullYear()} Kobra IA. Securing Editorial Financial Intelligence.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 1.5, sm: 2 },
                justifyContent: 'center',
              }}
            >
              {[
                { label: 'Política de privacidad', to: '/privacidad' },
                { label: 'Términos del servicio' },
                { label: 'Arquitectura de seguridad' },
              ].map(({ label, to }) => (
                <Link
                  key={label}
                  {...(to
                    ? { component: RouterLink, to }
                    : { href: '#', onClick: (e) => e.preventDefault() })}
                  variant="caption"
                  sx={{
                    color: 'rgba(18, 24, 38, 0.45)',
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': { color: C.blue, textDecoration: 'underline' },
                  }}
                >
                  {label}
                </Link>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    backgroundColor: INPUT_BG,
    '& fieldset': {
      borderColor: 'rgba(18, 24, 38, 0.08)',
    },
    '&:hover fieldset': {
      borderColor: `${C.blue}55`,
    },
    '&.Mui-focused fieldset': {
      borderColor: C.blue,
      borderWidth: 2,
    },
  },
};
